import dayjs from 'dayjs';
import enLocale from 'dayjs/locale/en';
import frLocale from 'dayjs/locale/fr';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import calendar from 'dayjs/plugin/calendar';
import duration from 'dayjs/plugin/duration';
import isoWeek from 'dayjs/plugin/isoWeek';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { useLanguages } from '@/hooks/languages/use-languages';

dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(localeData);
dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);
dayjs.extend(calendar);
dayjs.extend(relativeTime);

// The `localizedFormat` plugin attaches a `formats` map to whichever
// object is sitting at `dayjs.Ls.en` at extend time. But
// `useLocalizedDayjs` below does `instance.locale(enLocale)` with the
// bare imported locale object — that overwrites `dayjs.Ls.en` and wipes
// `formats`, crashing any later `LT` / `ll` token format call. Set the
// `formats` map directly on the imported objects so it survives.
type LocaleWithFormats = typeof enLocale & { formats?: Record<string, string> };
(enLocale as LocaleWithFormats).formats ??= {
  LTS: 'h:mm:ss A',
  LT: 'h:mm A',
  L: 'MM/DD/YYYY',
  LL: 'MMMM D, YYYY',
  LLL: 'MMMM D, YYYY h:mm A',
  LLLL: 'dddd, MMMM D, YYYY h:mm A',
};
(frLocale as LocaleWithFormats).formats ??= {
  LTS: 'HH:mm:ss',
  LT: 'HH:mm',
  L: 'DD/MM/YYYY',
  LL: 'D MMMM YYYY',
  LLL: 'D MMMM YYYY HH:mm',
  LLLL: 'dddd D MMMM YYYY HH:mm',
};

dayjs.tz.setDefault(dayjs.tz.guess());

export const useLocalizedDayjs = () => {
  const { locale } = useLanguages();

  return (date?: dayjs.ConfigType) => {
    const instance = dayjs(date);

    return instance.locale(locale === 'fr' ? frLocale : enLocale);
  };
};

/** Returns the locale-appropriate dayjs format token for "time of day".
 *  English uses 12-hour clock (e.g. `"2:30 PM"`); French (and every
 *  other locale, by intentional fallthrough) uses 24-hour (e.g.
 *  `"14:30"`). The fallthrough is deliberate: every locale Allo
 *  currently supports beyond EN is 24h-default. When adding a 12h
 *  locale (e.g. ES-MX, sometimes), promote to a `Set` of 12h locales
 *  rather than chaining `===`s. Pull from this hook instead of
 *  inlining the ternary per call site. */
export const useTimeFormat = (): string => {
  const { locale } = useLanguages();
  return locale === 'en' ? 'h:mm A' : 'HH:mm';
};
